/**
 * AnchoredLayer: a small panel drawn beside the control that opened it
 * (the Invoices page's row menu and its status legend).
 *
 * Drawn in the overlay layer (OverlayPortal), at fixed coordinates taken
 * from the opener, because the invoice table sits inside an
 * overflow-x-auto wrapper: a panel drawn inside it is clipped by that
 * wrapper and, on the last rows, scrolls inside it instead of showing.
 *
 * The panel sits under the opener, right edges aligned, and flips above
 * it when there is no room below. It is kept 16px inside the viewport
 * on both sides, so a phone never scrolls sideways to read it. It
 * follows the opener on scroll and resize, and closes on Escape (focus
 * back to the opener), a press outside both, or focus moving outside both.
 *
 * Until it has measured itself it waits off-screen, never hidden: a
 * hidden element cannot take focus, and the row menu focuses its first
 * item as it opens.
 */

import { type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { OverlayPortal } from '@/components/primitives/OverlayPortal';

/** The viewport margin the panel keeps on every side. */
const EDGE = 16;
/** The gap between the opener and the panel. */
const GAP = 4;

export interface AnchoredLayerProps {
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  /** The panel's classes (its width, padding and surface). */
  className: string;
  id?: string;
  testId?: string;
  role?: string;
  ariaLabel?: string;
  /** Move focus to the panel itself as it opens (a panel with nothing to
   *  operate, such as the status meanings, so a screen reader reads it
   *  next to its toggle rather than at the end of the page). */
  focusOnOpen?: boolean;
  children: ReactNode;
}

export function AnchoredLayer({
  anchorRef,
  onClose,
  className,
  id,
  testId,
  role,
  ariaLabel,
  focusOnOpen = false,
  children,
}: AnchoredLayerProps): ReactNode {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const place = (): void => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      const a = anchor.getBoundingClientRect();
      const width = panel.offsetWidth;
      const height = panel.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const left = Math.max(EDGE, Math.min(a.right - width, vw - width - EDGE));
      const below = a.bottom + GAP;
      const top = below + height > vh - EDGE && a.top - GAP - height >= EDGE ? a.top - GAP - height : below;
      setPos({ top, left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchorRef]);

  useEffect(() => {
    if (focusOnOpen) panelRef.current?.focus({ preventScroll: true });
  }, [focusOnOpen]);

  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      onClose();
      anchorRef.current?.focus();
    };
    // Keyboard travel out of the layer (Tab past it, or a dialog taking
    // focus) closes it too, so a layer is never left open behind focus.
    const onFocusIn = (e: FocusEvent): void => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [anchorRef, onClose]);

  return (
    <OverlayPortal>
      <div
        ref={panelRef}
        id={id}
        data-testid={testId}
        role={role}
        aria-label={ariaLabel}
        tabIndex={focusOnOpen ? -1 : undefined}
        style={{
          position: 'fixed',
          top: pos?.top ?? -10000,
          left: pos?.left ?? -10000,
          maxWidth: `calc(100vw - ${2 * EDGE}px)`,
        }}
        className={className}
      >
        {children}
      </div>
    </OverlayPortal>
  );
}
